<template>
  <ul class="flex flex-col gap-6 text-block">
    <li v-for="excess in excesses" :key="excess">
      <RichText :field="{ value: excess }" class="text-racq-med-grey text-base" />
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { computedAsync } from '@vueuse/core';
import { RichText, Field } from '@sitecore-jss/sitecore-jss-vue';
import { formatCurrency } from '@/utils/text-transforms';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { IQuote, QuoteTypeCodes, InsuranceQuoteType, INameCode } from '@/interfaces';
import { HQStepIds } from '@/constants/home-quote.const';
import { OccupancyTypes } from '@/constants/occupancy-types.const';
import { useReferenceDataStore } from '@/store';

const props = defineProps<{
  fields: {
    additionalExcess: Field<string>;
    petExcess: Field<string>;
    earthquakeExcess: Field<string>;
    portableItemExcess: Field<string>;
    mobilePhoneExcess: Field<string>;
    listedDriverAgeExcess: Field<string>;
    unlistedDriverAgeExcess: Field<string>;
    inexperiencedDriverExcess: Field<string>;
  };
  quote: Partial<IQuote>;
  stepId: string;
  isTestMode?: boolean;
}>();

const refDataStore = useReferenceDataStore();

const isLandlord = computed(
  () => props.quote.homeAndContentsQuote?.propertyOwnershipType?.value == OccupancyTypes[OccupancyTypes.LANDLORD]
);
const isHome = computed(() => props.quote.quoteType == InsuranceQuoteType.Home);
const isYourQuoteStep = computed(() => props.stepId === HQStepIds.YourQuote);

const additionalExcess = computed(() => {
  if (isYourQuoteStep.value) {
    return props.fields.additionalExcess?.value;
  }
  return replaceExcessAmount(
    props.fields.additionalExcess?.value,
    props.quote.homeAndContentsQuote?.ruleCalculations?.homeExcess?.value
  );
});

const petExcessRaw = ref<INameCode[]>();
const petExcess = computedAsync(
  async () => {
    if (props.isTestMode) {
      return props.fields.petExcess?.value;
    }
    if (isLandlord.value || isHome.value) {
      return undefined;
    }
    if (
      !isYourQuoteStep.value &&
      (props.quote.homeAndContentsQuote?.advancedCoverOptions?.numberOfPetsToCover?.value ?? 0) <= 0
    ) {
      return undefined;
    }
    if (!petExcessRaw.value) {
      petExcessRaw.value = await refDataStore.getPetExcesses(
        QuoteTypeCodes[props.quote.quoteType!],
        props.quote.homeAndContentsQuote?.propertyOwnershipType?.value ?? ''
      );
    }
    return replaceExcessAmount(props.fields.petExcess?.value, petExcessRaw.value?.at(0)?.code);
  },
  null,
  { lazy: true }
);

const earthquakeExcessRaw = ref<INameCode[]>();
const earthquakeExcess = computedAsync(
  async () => {
    if (props.isTestMode) {
      return props.fields.earthquakeExcess?.value;
    }
    if (!earthquakeExcessRaw.value) {
      earthquakeExcessRaw.value = await refDataStore.getEarthquakeExcesses(QuoteTypeCodes[props.quote.quoteType!]);
    }
    return replaceExcessAmount(props.fields.earthquakeExcess?.value, earthquakeExcessRaw.value?.at(0)?.code);
  },
  null,
  { lazy: true }
);

const portableExcessesRaw = ref<INameCode[]>();
const portableItemExcess = computedAsync(
  async () => {
    if (props.isTestMode) {
      return props.fields.portableItemExcess?.value;
    }
    if (isLandlord.value || isHome.value) {
      return undefined;
    }
    if (
      !props.quote.homeAndContentsQuote?.contentsQuote?.portableItemCoverGroupValueRange &&
      (props.quote.homeAndContentsQuote?.contentsQuote?.portableContentItems?.length ?? 0) <= 0
    ) {
      return undefined;
    }
    if (!portableExcessesRaw.value) {
      portableExcessesRaw.value = await refDataStore.getPortableExcesses(QuoteTypeCodes[props.quote.quoteType!]);
    }
    return replaceExcessAmount(props.fields.portableItemExcess?.value, portableExcessesRaw.value?.at(0)?.code);
  },
  null,
  { lazy: true }
);

const mobilePhoneExcessRaw = ref<INameCode[]>();
const mobilePhoneExcess = computedAsync(
  async () => {
    if (props.isTestMode) {
      return props.fields.mobilePhoneExcess?.value;
    }
    if (isLandlord.value || isHome.value) {
      return undefined;
    }
    if ((props.quote.homeAndContentsQuote?.contentsQuote?.insuredMobilePhones?.length ?? 0) <= 0) {
      return undefined;
    }
    if (!mobilePhoneExcessRaw.value) {
      mobilePhoneExcessRaw.value = await refDataStore.getPortableMobileExcesses(QuoteTypeCodes[props.quote.quoteType!]);
    }
    return replaceExcessAmount(props.fields.mobilePhoneExcess?.value, mobilePhoneExcessRaw.value?.at(0)?.code);
  },
  null,
  { lazy: true }
);

const listedDriverAgeExcess = computedAsync(
  async () => {
    return props.fields.listedDriverAgeExcess?.value;
  },
  null,
  { lazy: true }
);

const unlistedDriverAgeExcess = computedAsync(
  async () => {
    return props.fields.unlistedDriverAgeExcess?.value;
  },
  null,
  { lazy: true }
);

const inexperiencedDriverExcess = computedAsync(
  async () => {
    return props.fields.inexperiencedDriverExcess?.value;
  },
  null,
  { lazy: true }
);

const excesses = computed(() => {
  let result = [];
  if (petExcess.value) {
    result.push(petExcess.value);
  }
  if (earthquakeExcess.value) {
    result.push(earthquakeExcess.value);
  }
  if (portableItemExcess.value) {
    result.push(portableItemExcess.value);
  }
  if (mobilePhoneExcess.value) {
    result.push(mobilePhoneExcess.value);
  }
  if (additionalExcess.value) {
    result.push(additionalExcess.value);
  }
  if (listedDriverAgeExcess.value) {
    result.push(listedDriverAgeExcess.value);
  }
  if (unlistedDriverAgeExcess.value) {
    result.push(unlistedDriverAgeExcess.value);
  }
  if (inexperiencedDriverExcess.value) {
    result.push(inexperiencedDriverExcess.value);
  }
  return result;
});

function replaceExcessAmount(content?: string, amount?: string | number) {
  if (!amount || !content) return '';
  if (typeof amount === 'string') amount = Number(amount);
  return replaceBookmark('excessAmount', content, formatCurrency(amount));
}
</script>
